import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../services/index';
import { ContactFormData, PublicContact, DetailedContact } from '../../types/contact';
import Modal from '../../components/Modal';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import toast from 'react-hot-toast';
import { CustomSelect } from '../../components';
import { replaceWeekdaysInString, formatPhoneNumber } from '../../utils';
import { formatDateTime } from '../../utils/formatters';

// 문의 · Q&A 통합 페이지: FAQ + 문의하기 폼 + 내 문의 확인
const Contact: React.FC = () => {
  const { t } = useLanguage();

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  // ── 문의하기 폼 상태 ──
  const [formData, setFormData] = useState<ContactFormData>({
    nickname: '',
    password: '',
    phone: '',
    subject: '레슨 문의',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── FAQ 상태 ──
  const [openFaqItems, setOpenFaqItems] = useState<Set<number>>(new Set());

  // ── 내 문의 확인 상태 ──
  const [publicContacts, setPublicContacts] = useState<PublicContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [contactData, setContactData] = useState<DetailedContact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [activeTab, setActiveTab] = useState('전체');

  const toggleFaqItem = (index: number) => {
    const newOpenItems = new Set(openFaqItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenFaqItems(newOpenItems);
  };

  useEffect(() => {
    fetchPublicContacts();
  }, []);

  const fetchPublicContacts = async () => {
    try {
      setIsLoadingList(true);
      const response = await contactAPI.getPublicContacts();
      if (response.success && response.data) {
        setPublicContacts(response.data.contacts || []);
      }
    } catch {
      // 공개 문의 목록 조회 오류
    } finally {
      setIsLoadingList(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // 전화번호 필드는 자동 포맷팅 적용
    if (name === 'phone') {
      setFormData(prev => ({
        ...prev,
        [name]: formatPhoneNumber(value)
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 필수 필드 검증
    if (!formData.nickname || !formData.password || !formData.subject || !formData.message) {
      toast.error(t('fill_all_required'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await contactAPI.submitContact({
        ...formData,
        category: formData.subject,
        message: replaceWeekdaysInString(formData.message)
      });

      if (response.success) {
        setFormData({
          nickname: '',
          password: '',
          phone: '',
          subject: '레슨 문의',
          message: '',
        });
        toast.success(t('contact_submit_success'));
        // 문의 목록 갱신
        fetchPublicContacts();
      } else {
        toast.error(t('contact_submit_error'));
      }
    } catch {
      toast.error(t('contact_submit_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSelect = (contactId: string) => {
    setSelectedContactId(contactId);
    setPassword('');
    setContactData(null);
    setError('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContactId || !password) return;

    setIsLoading(true);
    setError('');
    setContactData(null);

    try {
      const response = await contactAPI.checkContact({
        contactId: selectedContactId,
        password: password
      });
      if (response.success && response.data) {
        setContactData(response.data);
        setShowPasswordModal(false);
        setShowResultModal(true);
      } else {
        setError(response.message || t('inquiry_check_failed'));
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || t('inquiry_check_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedContactId('');
    setPassword('');
    setError('');
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setContactData(null);
    setSelectedContactId('');
    setPassword('');
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return t('pending');
      case 'replied':
        return t('replied');
      case 'closed':
        return t('closed');
      default:
        return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'badge badge-pending';
      case 'replied':
        return 'badge badge-replied';
      case 'closed':
        return 'badge badge-closed';
      default:
        return 'badge badge-closed';
    }
  };

  const getFilteredContacts = () => {
    if (activeTab === '전체') {
      return publicContacts;
    }
    return publicContacts.filter(contact => contact.category === activeTab);
  };

  const tabs = [
    { display: t('tab_all'), value: '전체' },
    { display: t('lesson_inquiry'), value: '레슨 문의' },
    { display: t('fee_inquiry'), value: '수업료 문의' },
    { display: t('oneday_inquiry'), value: '원데이클래스 문의' },
    { display: t('facility_inquiry'), value: '시설 이용 문의' },
    { display: t('other_inquiry'), value: '기타' },
  ];

  const faqItems = [
    { question: t('faq_question_1'), answer: t('faq_answer_1') },
    { question: t('faq_question_2'), answer: t('faq_answer_2') },
    { question: t('faq_question_3'), answer: t('faq_answer_3') },
    { question: t('faq_question_4'), answer: t('faq_answer_4') },
    { question: t('faq_question_5'), answer: t('faq_answer_5') },
    { question: t('faq_question_6'), answer: t('faq_answer_6') },
  ];

  return (
    <div className="contact-page">
      <div className="page-header">
        <div className="page-badge">Contact · Q&A</div>
        <h1 className="page-title">{t('contact_qna')}</h1>
        <p className="page-description">{t('contact_subtitle')}</p>
      </div>

      <div className="page-content">
        <div className="container">

          {/* FAQ 섹션 */}
          <div className="form-section faq-section">
            <div className="section-header">
              <div className="section-label">FAQ</div>
              <h2 className="section-title">{t('faq')}</h2>
              <p className="section-subtitle">{t('faq_subtitle')}</p>
            </div>

            <div className="faq-grid">
              {faqItems.map((item, index) => (
                <div key={index} className={`faq-item ${openFaqItems.has(index) ? 'open' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaqItem(index)}>
                    <h3>{item.question}</h3>
                    <span className="faq-icon">+</span>
                  </div>
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 문의하기 폼 + 내 문의 확인 */}
          <div className="cq-layout">
            <form onSubmit={handleSubmit} className="contact-form cq-form-panel">
              <div className="cq-panel-heading">
                <h2 className="cq-panel-title">{t('contact_title')}</h2>
                <p className="cq-panel-subtitle">{t('password_description')}</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nickname" className="form-label">{t('nickname')}</label>
                  <input
                    type="text"
                    id="nickname"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder={t('nickname_placeholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">{t('password')}</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder={t('password_help')}
                    minLength={4}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label unrequired">{t('phone_optional')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="010-0000-0000"
                />
                <div className="form-help">
                  {t('phone_optional_help')}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">{t('inquiry_type')}</label>
                <CustomSelect
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                  placeholder={t('select_inquiry_type')}
                  options={[
                    { value: "레슨 문의", label: t('lesson_inquiry') },
                    { value: "수업료 문의", label: t('fee_inquiry') },
                    { value: "원데이클래스 문의", label: t('oneday_inquiry') },
                    { value: "시설 이용 문의", label: t('facility_inquiry') },
                    { value: "기타", label: t('other_inquiry') }
                  ]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">{t('inquiry_content')}</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows={5}
                  placeholder={t('inquiry_placeholder')}
                  required
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {t('sending')}
                    </>
                  ) : (
                    t('send_inquiry')
                  )}
                </button>
              </div>
            </form>

            <div className="cq-list-panel">
              <div className="cq-panel-heading">
                <h2 className="cq-panel-title">{t('my_inquiries_title')}</h2>
                <p className="cq-panel-subtitle">{t('inquiry_list_subtitle')}</p>
              </div>

              {/* 탭 메뉴 */}
              <div className="contact-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    className={`tab-button ${activeTab === tab.value ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.value)}
                  >
                    {tab.display}
                    <span className="tab-count">
                      {tab.value === '전체'
                        ? publicContacts.length
                        : publicContacts.filter(contact => contact.category === tab.value).length
                      }
                    </span>
                  </button>
                ))}
              </div>

              {isLoadingList ? (
                <div className="loading-state">
                  <span className="spinner"></span>
                  <p>{t('loading_inquiries')}</p>
                </div>
              ) : (
                <div className="contacts-list">
                  {getFilteredContacts().map((contact) => (
                    <div
                      key={contact.contactId}
                      className={`contact-card ${selectedContactId === contact.contactId ? 'selected' : ''}`}
                      onClick={() => handleContactSelect(contact.contactId)}
                    >
                      <div className="contact-header">
                        <div className="contact-title">                          <h3>{contact.subject}</h3>
                        </div>
                        <span className={getStatusClass(contact.status)}>
                          {getStatusText(contact.status)}
                        </span>
                      </div>

                      <div className="contact-meta">
                        <div className="meta-item">
                          <span className="meta-label">{t('author_label')}</span>
                          <span className="meta-value">{contact.nickname}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">{t('date_received_label')}</span>
                          <span className="meta-value">{formatDateTime(contact.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {getFilteredContacts().length === 0 && !isLoadingList && (
                    <div className="no-contacts">
                      <div className="empty-state">                        <h3>{activeTab === '전체' ? t('no_inquiries') : t('no_inquiries_category')}</h3>
                        <p>{t('first_inquiry')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 비밀번호 입력 모달 */}
      <Modal
        isOpen={showPasswordModal}
        onClose={handleClosePasswordModal}
        title={t('select_inquiry')}
        size="sm"
      >
        <div className="modal-content">
          <p className="modal-description">
            {t('password_description_modal')}
          </p>

          {error && (
            <div className="alert alert-error">
              <div className="alert-message">{error}</div>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="modal-password" className="form-label">{t('password')} *</label>
              <input
                type="password"
                id="modal-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder={t('password_modal_placeholder')}
                required
                autoFocus
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClosePasswordModal}
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || !password}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    {t('checking')}
                  </>
                ) : (
                  t('view_content')
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* 문의 내용 표시 모달 */}
      <Modal
        isOpen={showResultModal}
        onClose={handleCloseResultModal}
        title={t('contact_details')}
        size="lg"
      >
        {contactData && (
          <div className="modal-content">
            <div className="contact-details">
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('nickname')}:</label>
                  <span>{contactData.nickname}</span>
                </div>
                <div className="info-item">
                  <label>{t('category')}</label>
                  <span>{contactData.category}</span>
                </div>
                <div className="info-item">
                  <label>{t('received_date')}</label>
                  <span>{formatDateTime(contactData.createdAt)}</span>
                </div>
                <div className="info-item">
                  <label>{t('status')}:</label>
                  <span className={getStatusClass(contactData.status)}>
                    {getStatusText(contactData.status)}
                  </span>
                </div>
              </div>

              <div className="content-section">
                <div className="content-item">
                  <label>{t('title')}:</label>
                  <div className="content-text">{contactData.subject}</div>
                </div>
                <div className="content-item">
                  <label>{t('inquiry_content_detail')}</label>
                  <div className="content-text">{contactData.message}</div>
                </div>
              </div>

              {contactData.reply && (
                <div className="reply-section">
                  <div className="reply-header">
                    <h4>{t('reply')}</h4>
                    <span className="reply-date">
                      {formatDateTime(contactData.repliedAt || '')}
                    </span>
                  </div>
                  <div className="reply-content">
                    {contactData.reply}
                  </div>
                </div>
              )}

              {contactData.status === 'pending' && (
                <div className="pending-notice">                  <p>{t('pending_notice')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Contact;
